import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  AlertTriangle, 
  Archive, 
  Edit, 
  Eye, 
  MoreHorizontal, 
  Star, 
  Trash, 
  ShoppingBag, 
  RefreshCcw,
  XCircle
} from 'lucide-react';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import ProductCard from '@/components/product/card/ProductCard';
import { formatPrice } from '@/hooks/useProductDetail';
import ListingActionsOverlay from './ListingActionsOverlay';
import { useNavigate } from 'react-router-dom';
import { createProductUrl } from '@/utils/slugUtils';

interface ListingItemProps {
  listing: any;
  onView: (id: number) => void;
  onEdit?: (id: number) => void;
  onDelete?: (id: number, isArchived?: boolean) => void;
  onDeactivate?: (id: number) => void;
  onReactivate?: (id: number, isFromArchive?: boolean, originalId?: number) => void;
  onToggleSold?: (id: number, isSold: boolean) => void;
  onTogglePremium?: (id: number, isPremium: boolean) => void;
  isViewOnly?: boolean;
  isArchived?: boolean;
  isNewListing?: boolean;
  approval_status?: string;
}

const ListingItem = ({
  listing,
  onView,
  onEdit,
  onDelete,
  onDeactivate,
  onReactivate,
  onToggleSold,
  onTogglePremium,
  isViewOnly = false,
  isArchived = false,
  isNewListing = false,
  approval_status,
}: ListingItemProps) => {
  const navigate = useNavigate();
  
  // Extract category name from the nested structure
  const categoryName = listing.categories?.name || '';
  const subcategoryName = listing.subcategories?.name || '';

  // Calculate if this is a new listing based on creation timestamp
  const isRecentlyCreated = isNewListing || (
    listing.created_at && 
    (Date.now() - new Date(listing.created_at).getTime() < 60000) // Less than 1 minute old
  );

  const isRejected = approval_status === 'rejected';
  const isPending = approval_status === 'pending';

  // Handle view with SEO-friendly URL
  const handleView = (id: number) => {
    const productUrl = createProductUrl(id, listing.title);
    navigate(productUrl);
  };

  // Override the onView function to use SEO-friendly URLs
  const handleViewClick = () => {
    handleView(listing.id);
  };

  return (
    <div className="relative group">
      <ProductCard 
        id={listing.id.toString()}
        title={listing.title}
        price={listing.price}
        location={listing.location}
        imageUrl={listing.images?.[0] || ''}
        category={categoryName}
        subcategory={subcategoryName}
        isPremium={listing.is_premium}
        createdAt={listing.created_at}
        condition={listing.condition}
        deliveryOption={listing.delivery_option}
        user_id={listing.user_id}
        stock={listing.stock}
        is_price_negotiable={listing.is_price_negotiable}
        isLoading={isRecentlyCreated || isPending} 
        isNewUpload={isRecentlyCreated}
        // Vehicle specific properties
        make={listing.make}
        model={listing.model}
        year={listing.year}
        mileage={listing.mileage}
        transmission={listing.transmission}
      />
      
      {!isViewOnly && !isRejected && (
        <ListingActionsOverlay
          listingId={listing.id}
          isPremium={listing.is_premium}
          isSold={!!listing.is_sold}
          onView={handleView} // Use the SEO-friendly view handler
          onEdit={onEdit}
          onToggleSold={onToggleSold}
          onTogglePremium={onTogglePremium}
          onDelete={onDelete}
          onDeactivate={onDeactivate}
          onReactivate={onReactivate}
          isActive={!!listing.is_active}
          isArchived={isArchived}
          stock={listing.stock}
          originalId={listing.original_id}
          approvalStatus={approval_status}
        />
      )}
      
      {!isViewOnly && (
        <div className="absolute top-2 right-2 z-20">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="bg-white/80 backdrop-blur-sm hover:bg-white rounded-full">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleViewClick}>
                <Eye className="h-4 w-4 mr-2" /> Shiko
              </DropdownMenuItem>
              
              {onEdit && !isRejected && !isPending && (
                <DropdownMenuItem onClick={() => onEdit(listing.id)}>
                  <Edit className="h-4 w-4 mr-2" /> Ndrysho
                </DropdownMenuItem>
              )}
              
              {onToggleSold && !isRejected && !isPending && (
                <DropdownMenuItem onClick={() => onToggleSold(listing.id, !!listing.is_sold)}>
                  <ShoppingBag className="h-4 w-4 mr-2" />
                  {listing.is_sold ? 'Shëno si disponueshëm' : 'Shëno si të shitur'}
                </DropdownMenuItem>
              )}
              
              {onTogglePremium && !listing.is_premium && !isRejected && !isPending && (
                <DropdownMenuItem onClick={() => onTogglePremium(listing.id, !!listing.is_premium)}>
                  <Star className="h-4 w-4 mr-2" /> Promovo (Premium)
                </DropdownMenuItem>
              )}
              
              {onReactivate && !listing.is_active && !isRejected && !isPending && (
                <DropdownMenuItem onClick={() => onReactivate(listing.id, isArchived, listing.original_id)}>
                  <RefreshCcw className="h-4 w-4 mr-2" /> Riaktivizo
                </DropdownMenuItem>
              )}
              
              {onDeactivate && listing.is_active && !isRejected && !isPending && (
                <DropdownMenuItem onClick={() => onDeactivate(listing.id)}>
                  <Archive className="h-4 w-4 mr-2" /> Çaktivizo
                </DropdownMenuItem>
              )}
              
              {onDelete && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => onDelete(listing.id, isArchived)}
                    className="text-red-500 focus:text-red-500"
                  >
                    <Trash className="h-4 w-4 mr-2" /> Fshije
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {(isPending && !isArchived) && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-10">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-md text-center max-w-[80%]">
            <AlertTriangle className="h-8 w-8 text-yellow-500 mx-auto mb-2" />
            <h3 className="font-semibold mb-1">Në pritje të miratimit</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">Shpallja juaj po rishikohet nga stafi</p>
          </div>
        </div>
      )}

      {(isRejected && !isArchived) && (
        <div className="absolute inset-0 bg-red-500/30 backdrop-blur-sm flex items-center justify-center z-10">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-md text-center max-w-[80%] shadow-lg">
            <XCircle className="h-8 w-8 text-red-600 dark:text-red-500 mx-auto mb-2" />
            <h3 className="font-semibold text-red-700 dark:text-red-400 mb-1">Shpallja u refuzua</h3>
            <p className="text-xs text-gray-600 dark:text-gray-400">Kjo shpallje nuk i plotëson kushtet.</p>
          </div>
        </div>
      )}

      {isRecentlyCreated && !isPending && !isRejected && (
        <div className="absolute bottom-2 left-2 z-10">
          <Badge className="bg-green-500 text-white px-2 py-1">E re</Badge>
        </div>
      )}
    </div>
  );
};

export default ListingItem;
